import { observable, action, makeObservable } from 'mobx';
import { Deferred } from '../helper/PromiseHelper';

export enum LoadStatus {
    Pending,
    Loading,
    Complete,
    Errored,
}

export class LoadingTracker {
    @observable
    status: LoadStatus;

    private requestKey = 0;

    private currentRequest: Deferred<void> | undefined = undefined;
    lastError?: unknown = undefined;

    constructor(initialStatus = LoadStatus.Pending) {
        this.status = initialStatus;
        makeObservable(this);
    }

    asPromise() {
        if (!this.currentRequest) {
            this.currentRequest = new Deferred();
        }
        return this.currentRequest.promise;
    }

    /**
     * Runs `loader` as the latest request. `isCurrent` reports whether no newer `track()` has
     * started since, so a loader can skip writing a result that a newer request supersedes.
     */
    async track<T>(loader: (isCurrent: () => boolean) => T | Promise<T>): Promise<T> {
        const requestKey = (this.requestKey + 1) % 100000000;
        this.requestKey = requestKey;
        const isCurrent = () => this.requestKey === requestKey;
        // is this a fresh request
        if (this.status !== LoadStatus.Loading) {
            this.currentRequest = undefined;
        }

        this.setStatus(LoadStatus.Loading);
        let result: T;
        let error: unknown;
        // Track whether the loader threw with an explicit flag rather than inferring it from
        // `error`'s value — a loader that throws `undefined` must still count as a failure.
        let didThrow = false;
        try {
            result = await loader(isCurrent);
        } catch (e) {
            error = e;
            didThrow = true;
        }

        // Only the most recent request updates status/lastError/currentRequest;
        // superseded requests still re-throw so callers see an honest Promise<T>.
        //
        // Most call sites use `void store.load()`, so a rejection from a superseded
        // request has no .catch() attached and surfaces as an "Uncaught (in promise)"
        // warning in the browser console. This is intentional and expected — don't
        // "fix" it by swallowing errors here or by adding a global unhandled-rejection
        // suppressor (both would also hide real unhandled bugs). Production users
        // never see console output; this warning is dev-only noise.
        if (isCurrent()) {
            if (didThrow) {
                this.currentRequest?.reject(error);
                this.lastError = error;
                this.setStatus(LoadStatus.Errored);
            } else {
                this.currentRequest?.resolve();
                this.setStatus(LoadStatus.Complete);
            }
        }
        if (didThrow) {
            throw error; // re-throwing caught error
        }
        return result!;
    }

    @action
    setStatus(status: LoadStatus) {
        this.status = status;
    }
}
